import { SubscriptionAddressFullFragment } from "#graphql/supergraphTypes";
import { PaymentMethod } from "../../../../common/enums";
import { isUSA, redactAccountNumber } from "../../../../common/utils";
import { DetailedWirePaymentPrefObjectType } from "../../common/types";
import { type SelectedBankCountry } from "../../Context/types";
import { getIdFromCountry, getNameFromCountry, mapCountryToValueSafely } from "../../mappers";
import { type BankAddressInputType } from "../BankValidators/RoutingNumberInput/constants";
import { type WirePaymentPrefInputType } from "./constants";

const mapFormikAddressToGQLObject = (
  bankCountry: string,
  bankAddressInput: BankAddressInputType,
): SubscriptionAddressFullFragment => {
  const isUSACountry = isUSA(getNameFromCountry(bankCountry));

  return {
    city: bankAddressInput?.locality || null,
    country: {
      id: getIdFromCountry(bankCountry),
      name: getNameFromCountry(bankCountry),
      alpha2code: null,
      code: null,
    },
    postalCode: bankAddressInput?.postalCode?.trim() || null,
    region: !isUSACountry ? bankAddressInput?.region?.trim() || null : null,
    state: isUSACountry
      ? {
          __typename: "StateObjectType",
          // We are over-loading this object and should narrow it.
          code: bankAddressInput?.region?.trim() ?? "",
          // We are over-loading this object and should narrow it.
          id: bankAddressInput?.region?.trim() ?? "",
          name: "",
        }
      : null,
    street: bankAddressInput?.line1?.trim() || null,
    street2: bankAddressInput?.line2?.trim() || null,
    street3: null,
  };
};

/**
 * @note: be cognizant of nullish coalescing as we are converting to empty strings which are not then nullish coalesced
 * and will cause errors.
 */
export const mapFormStateToContextState = (
  values: WirePaymentPrefInputType,
): DetailedWirePaymentPrefObjectType => {
  return {
    __typename: "WirePaymentPrefObjectType",
    id: null,
    bank: {
      address: mapFormikAddressToGQLObject(values.bankCountry, values.bankAddress),
      abaNumber: values?.routingNumber?.trim() || null,
      // Capitalize the input before sending to BE since letters in Swift codes should always be capitalized
      swiftCode: values?.swiftCode?.trim().toUpperCase() || null,
      name: values?.bankName?.trim() ?? "",
      intlRoutingCode: values?.internationalRoutingNumber?.trim() || null,
    },
    intermediaryBank:
      values?.usesIntermediaryBank && values?.intermediaryBankCountry
        ? {
            address: mapFormikAddressToGQLObject(
              values?.intermediaryBankCountry?.trim(),
              values?.intermediaryBankAddress,
            ),
            abaNumber: values?.intermediaryRoutingNumber?.trim() || "",
            // Capitalize the input before sending to BE since letters in Swift codes should always be capitalized
            swiftCode: values?.intermediarySwiftCode?.trim().toUpperCase() || null,
            name: values?.intermediaryBankName?.trim() || "",
            intlRoutingCode: values?.intermediaryInternationalRoutingNumber?.trim() || null,
          }
        : null,
    bankInfoRedacted: `${values.bankName ?? "Bank"} account ending in -${
      values?.accountNumber ? redactAccountNumber(values?.accountNumber) : ""
    }`,
    beneficiaryAccountNumber: values?.accountNumber,
    beneficiaryAddress: mapFormikAddressToGQLObject(values?.country || "", {
      locality: values?.city,
      postalCode: values?.zip,
      region: values?.state || values?.province,
      line1: values.streetLine1,
      line2: values.streetLine2,
    }),
    beneficiaryName: values?.accountHolderName?.trim(),
    beneficiaryType: null,
    naturalPersonDateOfBirth: null,
    naturalPersonEmailAddress: null,
    naturalPersonFirstName: null,
    naturalPersonLastName: null,
    naturalPersonMiddleName: null,
    paymentMethod: PaymentMethod.Wire,
    paymentPrefId: null,
    referenceToBeneficiary: values?.referenceToBeneficiary?.trim() || null,
    wireObi: {
      field0: values?.obi?.field0?.trim() || null,
      field1: values?.obi?.field1?.trim() || null,
      field2: values?.obi?.field2?.trim() || null,
      field3: values?.obi?.field3?.trim() || null,
    },
    wireBbi: {
      field0: values?.bbi?.field0?.trim() || null,
      field1: values?.bbi?.field1?.trim() || null,
      field2: values?.bbi?.field2?.trim() || null,
      field3: values?.bbi?.field3?.trim() || null,
    },
  };
};

const mapGQLAddressToFormikState = ({
  gqlAddress,
  isUSA = false,
}: {
  gqlAddress?: SubscriptionAddressFullFragment;
  isUSA: boolean;
}): BankAddressInputType => {
  return {
    line1: gqlAddress?.street?.trim() || "",
    line2: gqlAddress?.street2?.trim() || "",
    locality: gqlAddress?.city?.trim() || "",
    postalCode: gqlAddress?.postalCode?.trim() || "",
    region: isUSA ? gqlAddress?.state?.code : gqlAddress?.region?.trim() || "",
  };
};

export const mapContextStateToFormState = (
  input?: DetailedWirePaymentPrefObjectType,
  selectedBankCountry?: SelectedBankCountry,
): WirePaymentPrefInputType => {
  const beneficiaryCountryName = input?.beneficiaryAddress?.country?.name;
  const bankAddressCountryName = input?.bank?.address?.country?.name;
  const intermediaryBankAddressCountryName = input?.intermediaryBank?.address?.country?.name;
  return {
    bankCountry: input?.bank?.address?.country?.id
      ? mapCountryToValueSafely(
          input.bank.address.country.id.trim(),
          input.bank.address.country.name?.trim(),
        )
      : selectedBankCountry?.id
        ? mapCountryToValueSafely(selectedBankCountry.id, selectedBankCountry.name)
        : "",
    bankName: input?.bank?.name?.trim() ?? "",
    bankAddress: mapGQLAddressToFormikState({
      gqlAddress: input?.bank?.address ?? undefined,
      isUSA: isUSA(bankAddressCountryName),
    }),
    routingNumber: input?.bank?.abaNumber?.trim() ?? "",
    swiftCode: input?.bank?.swiftCode?.trim() ?? "",
    internationalRoutingNumber: input?.bank?.intlRoutingCode?.trim() ?? undefined,
    usesIntermediaryBank: !!input?.intermediaryBank,
    intermediaryBankCountry: mapCountryToValueSafely(
      input?.intermediaryBank?.address?.country?.id?.trim(),
      input?.intermediaryBank?.address?.country?.name?.trim(),
    ),
    intermediaryBankName: input?.intermediaryBank?.name?.trim() ?? undefined,
    intermediaryBankAddress: mapGQLAddressToFormikState({
      gqlAddress: input?.intermediaryBank?.address ?? undefined,
      isUSA: isUSA(intermediaryBankAddressCountryName),
    }),
    intermediaryRoutingNumber: input?.intermediaryBank?.abaNumber?.trim() ?? "",
    intermediarySwiftCode: input?.intermediaryBank?.swiftCode?.trim() ?? "",
    intermediaryInternationalRoutingNumber:
      input?.intermediaryBank?.intlRoutingCode?.trim() ?? undefined,
    accountHolderName: input?.beneficiaryName?.trim() ?? "",
    accountNumber: input?.beneficiaryAccountNumber?.trim() ?? "",
    confirmAccountNumber: input?.beneficiaryAccountNumber?.trim() ?? "",
    country: mapCountryToValueSafely(
      input?.beneficiaryAddress?.country?.id?.trim(),
      beneficiaryCountryName,
    ),
    city: input?.beneficiaryAddress?.city?.trim() ?? undefined,
    zip: input?.beneficiaryAddress?.postalCode?.trim() ?? undefined,
    state:
      beneficiaryCountryName && isUSA(beneficiaryCountryName)
        ? input?.beneficiaryAddress?.state?.code?.trim() ?? undefined
        : undefined,
    province:
      beneficiaryCountryName && !isUSA(beneficiaryCountryName)
        ? input?.beneficiaryAddress?.region?.trim() ?? undefined
        : undefined,
    streetLine1: input?.beneficiaryAddress?.street?.trim() ?? undefined,
    streetLine2: input?.beneficiaryAddress?.street2?.trim() ?? undefined,
    referenceToBeneficiary: input?.referenceToBeneficiary?.trim() ?? undefined,
    obi: {
      field0: input?.wireObi?.field0 ?? undefined,
      field1: input?.wireObi?.field1 ?? undefined,
      field2: input?.wireObi?.field2 ?? undefined,
      field3: input?.wireObi?.field3 ?? undefined,
    },
    bbi: {
      field0: input?.wireBbi?.field0 ?? undefined,
      field1: input?.wireBbi?.field1 ?? undefined,
      field2: input?.wireBbi?.field2 ?? undefined,
      field3: input?.wireBbi?.field3 ?? undefined,
    },
  };
};
